/**
 * @fileOverview なでしこ3 plugin_system の分割ファイル
 *
 * 四則演算・論理演算・ビット演算の命令を定義する。
 * このファイルは単独のプラグインではなく、plugin_system.mts へマージされる。(#2351)
 */
export default {
    // @四則演算
    '足': {
        type: 'func',
        josi: [['に', 'と'], ['を']],
        isVariableJosi: false,
        pure: true,
        fn: function (a, b) {
            if (typeof (a) === 'bigint' || typeof (b) === 'bigint') {
                return BigInt(a) + BigInt(b);
            }
            return parseFloat(a) + parseFloat(b);
        }
    },
    '合計': {
        type: 'func',
        josi: [['と', 'を', 'の']],
        isVariableJosi: true,
        pure: true,
        fn: function (...a) {
            const sys = a.pop(); // remove NakoSystem
            if (a.length >= 1 && a[0] instanceof Array) {
                return sys.__exec('配列合計', [a[0], sys]);
            }
            let isBigInt = false;
            let sum = 0;
            for (const v of a) {
                if (typeof (v) === 'bigint') {
                    isBigInt = true;
                    break;
                }
                sum += parseFloat(v);
            }
            if (isBigInt) {
                let bigsum = 0n;
                for (const v of a) {
                    bigsum += BigInt(v);
                }
                return bigsum;
            }
            return sum;
        }
    },
    '引': {
        type: 'func',
        josi: [['から'], ['を']],
        pure: true,
        fn: function (a, b) {
            return a - b;
        }
    },
    '掛': {
        type: 'func',
        josi: [['に', 'と'], ['を']],
        pure: true,
        fn: function (a, b) {
            // 数値の掛け算
            if (typeof a === 'number') {
                return a * b;
            }
            // 文字列の掛け算(文字列の繰り返し)
            if (typeof a === 'string') {
                let s = '';
                for (let i = 0; i < parseInt(b); i++) {
                    s += a;
                }
                return s;
            }
            // 配列の繰り返し
            if (a instanceof Array) {
                const aa = [];
                for (let i = 0; i < parseInt(b); i++) {
                    aa.push(...a);
                }
                return aa;
            }
            return a * b;
        }
    },
    '倍': {
        type: 'func',
        josi: [['の', 'を'], ['']],
        pure: true,
        fn: function (a, b) {
            return a * b;
        }
    },
    '割': {
        type: 'func',
        josi: [['を'], ['で']],
        pure: true,
        fn: function (a, b) {
            return a / b;
        }
    },
    '割余': {
        type: 'func',
        josi: [['を'], ['で']],
        pure: true,
        fn: function (a, b) {
            return a % b;
        }
    },
    '偶数': {
        type: 'func',
        josi: [['が']],
        pure: true,
        fn: function (a) {
            return (parseInt(a) % 2 === 0);
        }
    },
    '奇数': {
        type: 'func',
        josi: [['が']],
        pure: true,
        fn: function (a) {
            return (parseInt(a) % 2 === 1);
        }
    },
    '二乗': {
        type: 'func',
        josi: [['の', 'を']],
        pure: true,
        fn: function (a) {
            return a * a;
        }
    },
    'べき乗': {
        type: 'func',
        josi: [['の'], ['の']],
        pure: true,
        fn: function (a, b) {
            return Math.pow(a, b);
        }
    },
    '以上': {
        type: 'func',
        josi: [['が'], ['']],
        pure: true,
        fn: function (a, b) {
            return a >= b;
        }
    },
    '以下': {
        type: 'func',
        josi: [['が'], ['']],
        pure: true,
        fn: function (a, b) {
            return a <= b;
        }
    },
    '未満': {
        type: 'func',
        josi: [['が'], ['']],
        pure: true,
        fn: function (a, b) {
            return a < b;
        }
    },
    '超': {
        type: 'func',
        josi: [['が'], ['']],
        pure: true,
        fn: function (a, b) {
            return a > b;
        }
    },
    '等': {
        type: 'func',
        josi: [['が'], ['と']],
        pure: true,
        fn: function (a, b) {
            return a === b;
        }
    },
    '等無': {
        type: 'func',
        josi: [['が'], ['と']],
        pure: true,
        fn: function (a, b) {
            return a !== b;
        }
    },
    '一致': {
        type: 'func',
        josi: [['が'], ['と']],
        pure: true,
        fn: function (a, b) {
            // オブジェクトの場合、JSONに変換して比較
            if (typeof (a) === 'object') {
                const jsonA = JSON.stringify(a);
                const jsonB = JSON.stringify(b);
                return jsonA === jsonB;
            }
            return a === b;
        }
    },
    '不一致': {
        type: 'func',
        josi: [['が'], ['と']],
        pure: true,
        fn: function (a, b) {
            // オブジェクトの場合、JSONに変換して比較
            if (typeof (a) === 'object') {
                const jsonA = JSON.stringify(a);
                const jsonB = JSON.stringify(b);
                return jsonA !== jsonB;
            }
            return a !== b;
        }
    },
    '範囲内': {
        type: 'func',
        josi: [['が'], ['から'], ['の', 'までの']],
        pure: true,
        fn: function (v, a, b) {
            return (a <= v) && (v <= b);
        }
    },
    '範囲': {
        type: 'func',
        josi: [['から'], ['の', 'までの']],
        pure: true,
        fn: function (a, b) {
            return {
                '先頭': a,
                '末尾': b
            };
        }
    },
    '連続加算': {
        type: 'func',
        josi: [['を'], ['に', 'と']],
        isVariableJosi: true,
        pure: true,
        fn: function (b, ...a) {
            a.pop(); // 必ず末尾に sys があるので、末尾のシステム変数を除外
            a.push(b);
            return a.reduce((p, c) => p + c);
        }
    },
    'MAX': {
        type: 'func',
        josi: [['の'], ['と']],
        isVariableJosi: true,
        pure: true,
        fn: function (b, ...a) {
            const sys = a.pop();
            return sys.__exec('最大値', [b, ...a, sys]);
        }
    },
    '最大値': {
        type: 'func',
        josi: [['の'], ['と']],
        isVariableJosi: true,
        pure: true,
        fn: function (b, ...a) {
            a.pop(); // 必ず末尾に sys があるので、末尾のシステム変数を除外
            a.push(b);
            return a.reduce((p, c) => Math.max(p, c));
        }
    },
    'MIN': {
        type: 'func',
        josi: [['の'], ['と']],
        isVariableJosi: true,
        pure: true,
        fn: function (b, ...a) {
            const sys = a.pop();
            return sys.__exec('最小値', [b, ...a, sys]);
        }
    },
    '最小値': {
        type: 'func',
        josi: [['の'], ['と']],
        isVariableJosi: true,
        pure: true,
        fn: function (b, ...a) {
            a.pop(); // 必ず末尾に sys があるので、末尾のシステム変数を除外
            a.push(b);
            return a.reduce((p, c) => Math.min(p, c));
        }
    },
    'CLAMP': {
        type: 'func',
        josi: [['の', 'を'], ['から'], ['までの', 'で']],
        pure: true,
        fn: function (x, a, b) {
            return Math.min(Math.max(x, a), b);
        }
    },
    // @論理演算
    '論理OR': {
        type: 'func',
        josi: [['と'], ['の']],
        pure: true,
        fn: function (a, b) {
            return (a || b);
        }
    },
    '論理AND': {
        type: 'func',
        josi: [['と'], ['の']],
        pure: true,
        fn: function (a, b) {
            return (a && b);
        }
    },
    '論理NOT': {
        type: 'func',
        josi: [['の']],
        pure: true,
        fn: function (v) {
            return (!v);
        }
    },
    // @ビット演算
    'OR': {
        type: 'func',
        josi: [['と'], ['の']],
        pure: true,
        fn: function (a, b) {
            return (a | b);
        }
    },
    'AND': {
        type: 'func',
        josi: [['と'], ['の']],
        pure: true,
        fn: function (a, b) {
            return (a & b);
        }
    },
    'XOR': {
        type: 'func',
        josi: [['と'], ['の']],
        pure: true,
        fn: function (a, b) {
            return (a ^ b);
        }
    },
    'NOT': {
        type: 'func',
        josi: [['の']],
        pure: true,
        fn: function (v) {
            return (~v);
        }
    },
    'SHIFT_L': {
        type: 'func',
        josi: [['を'], ['で']],
        pure: true,
        fn: function (v, a) {
            return (v << a);
        }
    },
    'SHIFT_R': {
        type: 'func',
        josi: [['を'], ['で']],
        pure: true,
        fn: function (v, a) {
            return (v >> a);
        }
    },
    'SHIFT_UR': {
        type: 'func',
        josi: [['を'], ['で']],
        pure: true,
        fn: function (v, a) {
            return (v >>> a);
        }
    },
};
